import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { orderBy } from 'lodash';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { ELoadStatus } from '../../../../state/state.interface';
import { HttpUtilitiesService, IBaseResponse, IGetManyResponse } from '../../../../utilities/http-utilities.service';
import {
  IAddItemWorkInstructionAssignment,
  IDeleteItemWorkInstructionAssignment,
  IItemWorkInstructionAssignment,
  IWorkInstruction,
} from '../../../settings/work-instructions/work-instructions.interface';
import { IGetItemWorkInstructionAssignment } from '../items-modal.interface';
import { IWorkInstructionItemCard } from './item-work-instruction-list.interface';

export interface IItemWorkInstructionListState {
  assignmentLoading: ELoadStatus;
  dataLoading: ELoadStatus;
  workInstructions: IWorkInstructionItemCard[];
}

@Injectable()
export class ItemWorkInstructionListStore extends ComponentStore<IItemWorkInstructionListState> {
  public readonly data$: Signal<IWorkInstructionItemCard[]> = this.selectSignal(
    (state: IItemWorkInstructionListState) => state.workInstructions || [],
  );

  private readonly assignmentStatus = this.selectSignal(({ assignmentLoading }) => assignmentLoading);
  private readonly dataStatus = this.selectSignal(({ dataLoading }) => dataLoading);
  public readonly assignmentDialogStatus = computed(() => {
    return this.httpUtilities.combineHttpStatusesIntoOne([this.assignmentStatus(), this.dataStatus()]);
  });

  readonly loadWorkInstruction = this.effect((trigger$: Observable<IGetItemWorkInstructionAssignment>) =>
    trigger$.pipe(
      switchMap((params: IGetItemWorkInstructionAssignment) => {
        this.patchState({ dataLoading: ELoadStatus.loading });

        return forkJoin({
          assignments: this.getItemWorkInstructionAssignments(params),
          workInstructions: this.getWorkInstructions(),
        }).pipe(
          tapResponse(
            ({ workInstructions, assignments }) => {
              const workInstructionCards: IWorkInstructionItemCard[] = workInstructions.data.map((workInstruction) => {
                return {
                  ...workInstruction,
                  assignmentId: assignments.data.find((assignment) => assignment.guideId === workInstruction.id)?.id,
                };
              });

              this.patchState({
                dataLoading: ELoadStatus.success,
                workInstructions: orderBy(workInstructionCards, ['assignmentId', 'createdAt']),
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly assignItemWorkInstruction = this.effect((trigger$: Observable<IAddItemWorkInstructionAssignment>) =>
    trigger$.pipe(
      switchMap((item: IAddItemWorkInstructionAssignment) => {
        this.patchState({ assignmentLoading: ELoadStatus.loading });

        return this.addItemWorkInstructionAssignment(item).pipe(
          tapResponse(
            () => this.patchState({ assignmentLoading: ELoadStatus.success }),
            () => {
              this.patchState({ assignmentLoading: ELoadStatus.failure });
            },
          ),
        );
      }),
    ),
  );

  readonly unassignItemWorkInstruction = this.effect((trigger$: Observable<IDeleteItemWorkInstructionAssignment>) =>
    trigger$.pipe(
      switchMap(({ id, startDateForRecurringUpdate }) => {
        this.patchState({ assignmentLoading: ELoadStatus.loading });
        let httpParams = new HttpParams();

        if (startDateForRecurringUpdate !== undefined) {
          httpParams = httpParams.set('startDateForRecurringUpdate', String(startDateForRecurringUpdate));
        }

        return this.deleteItemWorkInstructionAssignment(id, { params: httpParams }).pipe(
          tapResponse(
            () => this.patchState({ assignmentLoading: ELoadStatus.success }),
            () => {
              this.patchState({ assignmentLoading: ELoadStatus.failure });
            },
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      assignmentLoading: ELoadStatus.initial,
      dataLoading: ELoadStatus.initial,
      workInstructions: [],
    });
  }

  private getWorkInstructions(): Observable<IGetManyResponse<IWorkInstruction>> {
    const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters({
      fields: ['id', 'name', 'createdAt'],
      filters: [{ field: 'isActive', ids: [true] }],
      limit: 1000,
    });

    return this.http.get<IGetManyResponse<IWorkInstruction>>('guides', { params: httpParams });
  }

  private getItemWorkInstructionAssignments(
    params: IGetItemWorkInstructionAssignment,
  ): Observable<IGetManyResponse<IItemWorkInstructionAssignment>> {
    const httpParams: HttpParams = new HttpParams()
      .set('fields', 'id,guideId')
      .set('limit', 1000)
      .set('s', JSON.stringify({ $and: [{ boardId: { $eq: params.boardId } }, { itemId: { $eq: params.itemId } }] }));

    return this.http.get<IGetManyResponse<IItemWorkInstructionAssignment>>('item-guide-assignments', {
      params: httpParams,
    });
  }

  private addItemWorkInstructionAssignment(
    assignment: IAddItemWorkInstructionAssignment,
  ): Observable<IBaseResponse<IItemWorkInstructionAssignment>> {
    return this.http.post<IBaseResponse<IItemWorkInstructionAssignment>>('item-guide-assignments', assignment);
  }

  private deleteItemWorkInstructionAssignment(
    id: number,
    httpOptions: Parameters<HttpClient['get']>[1] = {},
  ): Observable<IBaseResponse<IItemWorkInstructionAssignment>> {
    return this.http.delete<IBaseResponse<IItemWorkInstructionAssignment>>(`item-guide-assignments/${id}`, httpOptions);
  }
}
